import React, { useCallback, useEffect, useMemo, useState } from "react";
import { KeyRound, Search, ChevronDown, ChevronRight, Award, CalendarDays, ListChecks, BookUser, Users2, Building2, School, Sparkles, Layers, BookOpen, FolderArchive, CalendarCheck, CalendarX2, HeartHandshake, Drama, Clapperboard } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import CollapsibleCard from "@/components/CollapsibleCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PLAN_KEYS, PLAN_LABEL, PLAN_COLOR } from "@/lib/plans";

const FEATURE_META = [
  { key: "merit_tracking", label: "Merit Tracking", icon: Award },
  { key: "schedule", label: "Daily Schedule", icon: CalendarDays },
  { key: "choices", label: "Choices", icon: ListChecks },
  { key: "phonebook", label: "Phonebook", icon: BookUser },
  { key: "rally_books", label: "Rally Books", icon: BookOpen },
  { key: "document_depot", label: "Document Depot", icon: FolderArchive },
  { key: "attendance", label: "Attendance", icon: CalendarCheck, master: true },
  { key: "conflicts", label: "Conflict Notifications", icon: CalendarX2, master: true },
  { key: "volunteers", label: "Volunteer Sign-Up", icon: HeartHandshake, master: true },
  { key: "thespian", label: "Thespian Point System", icon: Drama, master: true },
  { key: "show_notes", label: "Show Notes", icon: Clapperboard, master: true },
  { key: "animations", label: "Animations", icon: Sparkles },
  { key: "parent_account", label: "Parent Account", icon: Users2 },
  { key: "district_admin_account", label: "District Administrator Account", icon: Building2 },
  { key: "campus_admin_account", label: "Campus Administrator Account", icon: School },
];

const PLAN_FILTERS = [...PLAN_KEYS, "custom"];

function eq(a, b, keys) {
  return keys.every((k) => !!a[k] === !!b[k]);
}

export default function LicenseManagement({ defaultOpen = false, initialPlan = "" }) {
  const [items, setItems] = useState([]);
  const [presets, setPresets] = useState({});
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState(PLAN_FILTERS.includes(initialPlan) ? initialPlan : "all");
  useEffect(() => { if (PLAN_FILTERS.includes(initialPlan)) setPlanFilter(initialPlan); }, [initialPlan]);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  const [bulkScope, setBulkScope] = useState("district");
  const [bulkTarget, setBulkTarget] = useState("");
  const [bulkPlan, setBulkPlan] = useState("full");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/licenses")
      .then(({ data }) => { setItems(data.items || []); setPresets(data.presets || {}); setKeys(data.feature_keys || []); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const computePlan = (ent) => {
    for (const name of ["base", "standard", "full", "theatre"]) {
      if (presets[name] && eq(ent, presets[name], keys)) return name;
    }
    return "custom";
  };

  const patch = async (teamId, payload, optimistic) => {
    setBusy((b) => ({ ...b, [teamId]: true }));
    const prev = items;
    if (optimistic) setItems((list) => list.map((t) => (t.team_id === teamId ? { ...t, ...optimistic } : t)));
    try {
      const { data } = await api.put(`/admin/licenses/${teamId}`, payload);
      setItems((list) => list.map((t) => (t.team_id === teamId ? { ...t, plan: data.plan, entitlements: data.entitlements } : t)));
    } catch (err) {
      setItems(prev);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, [teamId]: false }));
    }
  };

  const selectPlan = (team) => (plan) => {
    if (plan === "custom" || plan === team.plan) return;
    const ent = { ...presets[plan] };
    patch(team.team_id, { plan }, { plan, entitlements: ent });
    toast.success(`${team.team_name}: ${PLAN_LABEL[plan]} plan`);
  };

  const toggleFeature = (team, key) => (val) => {
    const ent = { ...team.entitlements, [key]: val };
    patch(team.team_id, { entitlements: { [key]: val } }, { entitlements: ent, plan: computePlan(ent) });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => (planFilter === "all" || (t.plan || "custom") === planFilter) && (!q ||
      [t.team_name, t.team_code, t.campus_name, t.district_name].filter(Boolean).some((s) => s.toLowerCase().includes(q))));
  }, [items, search, planFilter]);

  const targetOptions = useMemo(() => {
    const map = new Map();
    for (const t of items) {
      if (bulkScope === "district") {
        if (t.district_id && !map.has(t.district_id)) map.set(t.district_id, t.district_name || "Unnamed district");
      } else if (t.campus_id && !map.has(t.campus_id)) {
        map.set(t.campus_id, [t.campus_name || "Unnamed campus", t.district_name].filter(Boolean).join(" · "));
      }
    }
    return Array.from(map, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [items, bulkScope]);

  const affectedCount = useMemo(() =>
    items.filter((t) => (bulkScope === "district" ? t.district_id : t.campus_id) === bulkTarget).length,
    [items, bulkScope, bulkTarget]);

  const applyBulk = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/licenses/bulk", { scope: bulkScope, scope_id: bulkTarget, plan: bulkPlan });
      toast.success(`${PLAN_LABEL[bulkPlan]} plan applied to ${data.updated} team${data.updated === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBulkBusy(false); }
  };

  const targetLabel = targetOptions.find((o) => o.id === bulkTarget)?.label || "";

  return (
    <CollapsibleCard title="License Management" icon={KeyRound} testId="settings-license-management-card" maxWidthClass="max-w-3xl" defaultOpen={defaultOpen}>
      <p className="mb-4 text-xs text-slate-400">Set a plan and toggle features per organization (team). Changes apply immediately to that team only.</p>

      <div className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4" data-testid="license-bulk-section">
        <div className="mb-3 flex items-center gap-2">
          <Layers className="h-4 w-4 text-indigo-600" />
          <p className="text-sm font-semibold text-slate-700">Bulk apply a plan</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Scope</label>
            <Select value={bulkScope} onValueChange={(v) => { setBulkScope(v); setBulkTarget(""); }}>
              <SelectTrigger className="h-9 w-36" data-testid="license-bulk-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="district">District</SelectItem>
                <SelectItem value="campus">Campus</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">{bulkScope === "district" ? "District" : "Campus"}</label>
            <Select value={bulkTarget} onValueChange={setBulkTarget}>
              <SelectTrigger className="h-9 w-56" data-testid="license-bulk-target"><SelectValue placeholder={`Select ${bulkScope}…`} /></SelectTrigger>
              <SelectContent>
                {targetOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Plan</label>
            <Select value={bulkPlan} onValueChange={setBulkPlan}>
              <SelectTrigger className="h-9 w-36" data-testid="license-bulk-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="base">Base</SelectItem>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="full">Full</SelectItem>
                <SelectItem value="theatre">Theatre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="h-9" disabled={!bulkTarget || bulkBusy || affectedCount === 0} data-testid="license-bulk-apply">
                {bulkBusy ? "Applying…" : "Apply to all"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="license-bulk-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Apply {PLAN_LABEL[bulkPlan]} plan?</AlertDialogTitle>
                <AlertDialogDescription>
                  This overwrites the license for <strong>all {affectedCount} team{affectedCount === 1 ? "" : "s"}</strong> in {bulkScope} <strong>{targetLabel}</strong>, replacing any custom feature settings on those teams. This cannot be undone in bulk.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="license-bulk-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={applyBulk} data-testid="license-bulk-confirm-btn">Apply to {affectedCount} team{affectedCount === 1 ? "" : "s"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        {bulkTarget ? <p className="mt-2 text-xs text-slate-500" data-testid="license-bulk-count">{affectedCount} team{affectedCount === 1 ? "" : "s"} will be updated.</p> : null}
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search team, campus or district…" className="pl-9" data-testid="license-search-input" />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="sm:w-44" data-testid="license-plan-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="license-plan-filter-all">All plans</SelectItem>
            {PLAN_FILTERS.map((p) => <SelectItem key={p} value={p} data-testid={`license-plan-filter-${p}`}>{PLAN_LABEL[p]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!loading && planFilter !== "all" && (
        <p className="mt-2 text-xs text-slate-500" data-testid="license-plan-filter-count">
          Showing {filtered.length} team{filtered.length === 1 ? "" : "s"} on the {PLAN_LABEL[planFilter]} plan ·{" "}
          <button type="button" className="font-semibold text-sky-600 hover:underline" onClick={() => setPlanFilter("all")} data-testid="license-plan-filter-clear">Show all</button>
        </p>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-slate-400">Loading organizations…</p>
      ) : filtered.length === 0 ? (
        <p className="mt-6 text-sm text-slate-400" data-testid="license-empty">{planFilter !== "all" && !search.trim() ? `No teams on the ${PLAN_LABEL[planFilter]} plan.` : "No organizations match your search."}</p>
      ) : (
        <div className="mt-4 max-h-[360px] space-y-2 overflow-y-auto pr-1" data-testid="license-team-list">
          {filtered.map((team) => {
            const isOpen = !!expanded[team.team_id];
            const disabled = !!busy[team.team_id];
            return (
              <div key={team.team_id} className="rounded-lg border border-slate-200" data-testid={`license-team-${team.team_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded((e) => ({ ...e, [team.team_id]: !e[team.team_id] }))}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  data-testid={`license-team-toggle-${team.team_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{team.team_name}</p>
                      <p className="truncate text-xs text-slate-400">{[team.campus_name, team.district_name].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                  </div>
                  <Badge className={`shrink-0 border-0 ${PLAN_COLOR[team.plan] || PLAN_COLOR.custom}`} data-testid={`license-plan-badge-${team.team_id}`}>{PLAN_LABEL[team.plan] || "Custom"}</Badge>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 px-4 py-4">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="text-xs font-medium text-slate-500">Plan preset</span>
                      <Select value={team.plan} onValueChange={selectPlan(team)} disabled={disabled}>
                        <SelectTrigger className="h-9 w-40" data-testid={`license-plan-select-${team.team_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="base">Base</SelectItem>
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="full">Full</SelectItem>
                          <SelectItem value="theatre" data-testid={`license-plan-option-theatre-${team.team_id}`}>Theatre</SelectItem>
                          <SelectItem value="custom" disabled>Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      {FEATURE_META.map((f) => (
                        <div key={f.key} className="flex items-center justify-between gap-4 rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2" data-testid={`license-feature-row-${team.team_id}-${f.key}`}>
                          <div className="flex items-center gap-2">
                            <f.icon className="h-4 w-4 text-slate-500" />
                            <span className="text-sm text-slate-700">{f.label}</span>
                            {f.master && <span className="text-[11px] text-slate-400" data-testid={`license-master-note-${team.team_id}-${f.key}`}>· also needs the switch above</span>}
                          </div>
                          <Switch
                            checked={!!team.entitlements[f.key]}
                            onCheckedChange={toggleFeature(team, f.key)}
                            disabled={disabled}
                            data-testid={`license-toggle-${team.team_id}-${f.key}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleCard>
  );
}
